import { db } from './config';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
  deleteField,
  Timestamp,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import type { Appointment } from '@/types/models';
import { SecretaryBriefingService } from './secretaryBriefing';
import { AppointmentHistoryService } from './appointmentHistory';

/**
 * `createdAt` é gravado com `serverTimestamp()` e volta do Firestore como
 * `Timestamp`. O tipo `Appointment.createdAt` é `number` (ms epoch), então
 * normalizamos na leitura pra alinhar com o contrato — sem isso,
 * `new Date(a.createdAt)` no consumidor produz Invalid Date.
 *
 * Exportada pra consumidores com listener próprio (ex.: DayAgendaDrawer,
 * que assina os agendamentos do dia em tempo real) normalizarem igual.
 */
export function normalizeAppointment(raw: unknown): Appointment {
  const data = raw as Appointment & { createdAt: unknown };
  const ts = data.createdAt as Timestamp | number | undefined;
  let createdAtMs: number;
  if (typeof ts === 'number') {
    createdAtMs = ts;
  } else if (ts && typeof ts.toMillis === 'function') {
    createdAtMs = ts.toMillis();
  } else {
    createdAtMs = 0;
  }
  return { ...data, createdAt: createdAtMs };
}

// T092: Default overbooking limits per FR-042
const DEFAULT_OVERBOOKING_LIMIT = 2;
const MAX_OVERBOOKING_LIMIT = 5;

// T094: Exam prep instructions per type
const EXAM_PREP_INSTRUCTIONS: Record<string, string> = {
  OCT: 'Não é necessário preparo. Compareça 10 minutos antes do horário.',
  ANGIOGRAFIA: 'Realizar jejum de 4 horas. Trazer acompanhante. Informar alergias.',
  CAMPIMETRIA: 'Não usar colírio antes do exame. Trazer óculos de uso habitual.',
  TOPOGRAFIA: 'Não usar lentes de contato 7 dias antes (rígidas) ou 3 dias antes (gelatinosas).',
  RETINOGRAFIA: 'Será necessário dilatação pupilar. Trazer acompanhante e óculos escuros.',
  BIOMETRIA: 'Suspender lentes de contato 3 dias antes. Não é necessário jejum.',
  PAQUIMETRIA: 'Suspender lentes de contato 3 dias antes.',
};

/** Ordem de relevância dos agendamentos de HOJE de um paciente (o primeiro
 *  é o "ativo"). Compartilhada pela busca única e pelo listener. */
const TODAY_STATUS_ORDER: Record<string, number> = {
  EM_ATENDIMENTO: 0,
  EM_ESPERA: 1,
  CONFIRMADA: 2,
  AGENDADA: 3,
  PENDENTE: 4,
  CONCLUIDA: 5,
};

/** Query dos agendamentos do paciente no dia de HOJE (janela local). */
const todayAppointmentsQuery = (clinicId: string, patientId: string) => {
  const now = new Date();
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
  return query(
    collection(db, 'clinics', clinicId, 'appointments'),
    where('patientId', '==', patientId),
    where('date', '>=', dayStart),
    where('date', '<=', dayEnd),
  );
};

/** Normaliza + descarta cancelados/faltas/soft-deletes + ordena por
 *  relevância (status) e horário. `docs` = `snap.docs` de get/onSnapshot. */
const relevantTodayAppointments = (docs: Array<{ data(): unknown }>): Appointment[] =>
  docs
    .map((d) => normalizeAppointment(d.data()))
    .filter((a) => !a.isDeleted && a.status !== 'CANCELADA' && a.status !== 'FALTOU')
    .sort((a, b) => {
      const oa = TODAY_STATUS_ORDER[a.status] ?? 99;
      const ob = TODAY_STATUS_ORDER[b.status] ?? 99;
      if (oa !== ob) return oa - ob;
      // Mesmo status → mais cedo primeiro.
      return a.startTime.localeCompare(b.startTime);
    });

export const AppointmentService = {
  async createAppointment(
    clinicId: string,
    data: Omit<Appointment, 'id' | 'createdAt'>,
  ): Promise<Appointment> {
    // T092: Validate overbooking limit
    if (data.isOverbooking) {
      const overbookCount = await this.countOverbookings(
        clinicId,
        data.professionalId!,
        data.date,
        data.startTime,
      );
      const limit = data.overbookingLimit ?? DEFAULT_OVERBOOKING_LIMIT;
      if (overbookCount >= Math.min(limit, MAX_OVERBOOKING_LIMIT)) {
        throw new Error(
          `Limite de encaixe atingido (${limit} por slot). Próximo horário disponível sugerido.`,
        );
      }
    }

    // T093: Validate convênio quota
    if (data.insurerId) {
      const quotaOk = await this.checkConvenioQuota(clinicId, data.insurerId, data.professionalId!);
      if (!quotaOk) {
        throw new Error(
          'Cota mensal do convênio atingida para esta agenda. Slot indisponível para este convênio.',
        );
      }
    }

    // T094: Auto-add prep instructions for exams
    let prepInstructions = data.prepInstructions;
    if (data.type === 'EXAME' && !prepInstructions && data.examType) {
      prepInstructions = EXAM_PREP_INSTRUCTIONS[data.examType.toUpperCase()] ?? undefined;
    }

    const newDocRef = doc(collection(db, 'clinics', clinicId, 'appointments'));
    // Filtra undefined antes do setDoc — Firestore rejeita campos `undefined`.
    const rawAppointment: Record<string, unknown> = {
      ...data,
      id: newDocRef.id,
      prepInstructions,
      reminderSent: false,
      createdAt: serverTimestamp(),
    };
    const appointmentData = Object.fromEntries(
      Object.entries(rawAppointment).filter(([, v]) => v !== undefined),
    );
    await setDoc(newDocRef, appointmentData);
    return appointmentData as unknown as Appointment;
  },

  async updateAppointment(clinicId: string, appointmentId: string, data: Partial<Appointment>) {
    const docRef = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    // Filtra undefined antes do updateDoc — Firestore rejeita campos `undefined`.
    const rawUpdate: Record<string, unknown> = { ...data, updatedAt: serverTimestamp() };
    const cleanUpdate = Object.fromEntries(
      Object.entries(rawUpdate).filter(([, v]) => v !== undefined),
    );
    await updateDoc(docRef, cleanUpdate);
  },

  async getAppointment(clinicId: string, appointmentId: string) {
    const docRef = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return normalizeAppointment(snap.data());
  },

  async getAppointmentsByDateRange(clinicId: string, startDate: number, endDate: number) {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => normalizeAppointment(d.data()))
      .filter((a) => !a.isDeleted);
  },

  /**
   * Versão em tempo real de getAppointmentsByDateRange — usada pela Agenda
   * (visões Dia/Lista) pra refletir mudanças feitas em outra máquina sem
   * precisar recarregar a página. Retorna a função de unsubscribe.
   */
  subscribeToAppointmentsByDateRange(
    clinicId: string,
    startDate: number,
    endDate: number,
    callback: (appointments: Appointment[]) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
    );
    return onSnapshot(
      q,
      (snap) => callback(
        snap.docs
          .map((d) => normalizeAppointment(d.data()))
          .filter((a) => !a.isDeleted),
      ),
      (err) => {
        console.error('[appointments] subscribeToAppointmentsByDateRange:', err);
        onError?.(err);
      },
    );
  },

  /**
   * UM agendamento em tempo real (`null` = o doc não existe). O painel
   * lateral da agenda assina o agendamento aberto pra não ficar preso na
   * foto do clique — cobrança, convênio, status e "próximo" gravados dali
   * mesmo ou de outra máquina chegam na hora.
   */
  subscribeToAppointment(
    clinicId: string,
    appointmentId: string,
    callback: (appointment: Appointment | null) => void,
  ): Unsubscribe {
    return onSnapshot(
      doc(db, 'clinics', clinicId, 'appointments', appointmentId),
      (snap) => callback(snap.exists() ? { ...normalizeAppointment(snap.data()), id: snap.id } : null),
      (err) => console.error('[appointments] subscribeToAppointment:', err),
    );
  },

  /**
   * Atendimentos CONCLUIDA com `date >= sinceMs`. Usado pelo relatório de
   * pacientes inativos — só consulta concluída conta como atividade
   * (cancelamentos e faltas não).
   */
  async getCompletedAppointmentsSince(clinicId: string, sinceMs: number) {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('status', '==', 'CONCLUIDA'),
      where('date', '>=', sinceMs),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => normalizeAppointment(d.data()))
      .filter((a) => !a.isDeleted);
  },

  /**
   * Lista agendamentos pela data de criação (quando o cadastro foi feito),
   * não pela data marcada da consulta. Usado pelo painel "Agendamentos
   * por Data" no modo "Data em que foi marcado" — útil pra ver o que a
   * recepção/secretária marcou em determinado dia, independente de quando
   * a consulta acontecerá.
   *
   * `createdAt` é gravado como `serverTimestamp()`, então a comparação
   * usa `Timestamp.fromMillis` pra casar com o tipo no Firestore.
   */
  async getAppointmentsByCreatedAtRange(clinicId: string, startMs: number, endMs: number) {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('createdAt', '>=', Timestamp.fromMillis(startMs)),
      where('createdAt', '<=', Timestamp.fromMillis(endMs)),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => normalizeAppointment(d.data()))
      .filter((a) => !a.isDeleted);
  },

  async getAppointmentsByPatient(clinicId: string, patientId: string) {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('patientId', '==', patientId),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => normalizeAppointment(d.data()))
      .filter((a) => !a.isDeleted);
  },

  /**
   * Propaga uma mudança de nome do paciente pros agendamentos dele.
   *
   * `patientName` é denormalizado no doc do agendamento — snapshot gravado
   * na criação (ver `createAppointment` / `AppointmentFormModal`). A agenda
   * lê esse campo direto, então editar o nome no cadastro não reflete na
   * agenda sem esta sincronização. Atualiza só os docs cujo nome divergiu
   * (ignora soft-deletados), em lotes de até 450 — o `writeBatch` aceita 500.
   *
   * Escopo: todos os agendamentos do paciente (passado e futuro) — é a
   * mesma pessoa, o nome só estava errado/desatualizado. Retorna quantos
   * docs foram atualizados. Não-fatal pro chamador: o save do cadastro não
   * deve rolar de volta se isto falhar.
   */
  async syncPatientNameOnAppointments(
    clinicId: string,
    patientId: string,
    newName: string,
  ): Promise<number> {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('patientId', '==', patientId),
    );
    const snapshot = await getDocs(q);
    const stale = snapshot.docs.filter((d) => {
      const data = d.data() as Appointment;
      return !data.isDeleted && data.patientName !== newName;
    });
    if (stale.length === 0) return 0;

    const CHUNK = 450;
    for (let i = 0; i < stale.length; i += CHUNK) {
      const batch = writeBatch(db);
      for (const d of stale.slice(i, i + CHUNK)) {
        batch.update(d.ref, { patientName: newName, updatedAt: serverTimestamp() });
      }
      await batch.commit();
    }
    return stale.length;
  },

  async cancelAppointment(clinicId: string, appointmentId: string) {
    // Limpa flag de "próximo" — paciente cancelado não pode ser o próximo.
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      status: 'CANCELADA',
      isNextUp: deleteField(),
      nextUpMarkedAt: deleteField(),
      nextUpMarkedBy: deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Soft delete — "Desmarcar" remove o agendamento da view da agenda mas
   * preserva o registro no Firestore pra auditoria. Diferente do
   * `cancelAppointment` (que só muda status pra CANCELADA, mantendo visível).
   *
   * Reaproveita `allow update` das rules — não precisa de delete permission.
   */
  async softDeleteAppointment(
    clinicId: string,
    appointmentId: string,
    actorUid: string,
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      isDeleted: true,
      deletedAt: Date.now(),
      deletedBy: actorUid,
      isNextUp: deleteField(),
      nextUpMarkedAt: deleteField(),
      nextUpMarkedBy: deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Inicia o cronômetro de atendimento. Coloca status em EM_ATENDIMENTO
   * e grava o timestamp de início. Se já houver um attendanceStartedAt
   * (atendimento foi reiniciado), preserva o original — assim a duração
   * total reflete o tempo desde o primeiro clique em "Iniciar".
   */
  async startAttendance(
    clinicId: string,
    appointmentId: string,
  ): Promise<number> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = snap.data() as Appointment;
    const startedAt = current.attendanceStartedAt ?? Date.now();
    // Limpa "próximo" — paciente já entrou em atendimento, não faz mais
    // sentido manter o destaque. Usa updateDoc direto pra permitir
    // deleteField() (que updateAppointment não suporta).
    await updateDoc(ref, {
      status: 'EM_ATENDIMENTO',
      attendanceStartedAt: startedAt,
      isNextUp: deleteField(),
      nextUpMarkedAt: deleteField(),
      nextUpMarkedBy: deleteField(),
      updatedAt: serverTimestamp(),
    });
    return startedAt;
  },

  /**
   * Conclui o atendimento. Marca status CONCLUIDA, grava timestamp final
   * e calcula a duração total em segundos pra exibição posterior.
   *
   * Logo após, calcula o "briefing pra secretária" — lê as prescrições
   * criadas durante a janela do atendimento (exames, cirurgias, retorno)
   * e grava `secretaryBriefing` no próprio appointment quando há algo a
   * orientar. Falhas no briefing não bloqueiam a finalização — é um
   * efeito colateral best-effort, logado em caso de erro.
   */
  async endAttendance(
    clinicId: string,
    appointmentId: string,
  ): Promise<{ durationSec: number; endedAt: number }> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = snap.data() as Appointment;
    const endedAt = Date.now();
    const startedAt = current.attendanceStartedAt ?? endedAt;
    const durationSec = Math.max(0, Math.round((endedAt - startedAt) / 1000));
    await this.updateAppointment(clinicId, appointmentId, {
      status: 'CONCLUIDA',
      attendanceStartedAt: startedAt,
      attendanceEndedAt: endedAt,
      attendanceDurationSec: durationSec,
    });
    // Gera o briefing pós-atendimento (não bloqueia em caso de erro).
    await SecretaryBriefingService.finalizeBriefing(clinicId, appointmentId);
    return { durationSec, endedAt };
  },

  /**
   * Retoma um atendimento já concluído. Volta status pra EM_ATENDIMENTO,
   * limpa `attendanceEndedAt`/`attendanceDurationSec` e desloca
   * `attendanceStartedAt` pra `now - durationAnterior` — assim o
   * cronômetro continua exatamente de onde parou (em vez de saltar pelo
   * tempo de pausa, o que seria confuso se a retomada acontecer horas
   * depois). Quando o atendimento for concluído de novo, `endAttendance`
   * regrava os campos finais e o briefing pra secretária.
   */
  async resumeAttendance(
    clinicId: string,
    appointmentId: string,
  ): Promise<number> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = snap.data() as Appointment;
    if (current.status !== 'CONCLUIDA') {
      throw new Error('Só é possível retomar um atendimento concluído.');
    }
    const previousDurationMs = Math.max(0, (current.attendanceDurationSec ?? 0) * 1000);
    const newStartedAt = Date.now() - previousDurationMs;
    await updateDoc(ref, {
      status: 'EM_ATENDIMENTO',
      attendanceStartedAt: newStartedAt,
      attendanceEndedAt: deleteField(),
      attendanceDurationSec: deleteField(),
      updatedAt: serverTimestamp(),
    });
    return newStartedAt;
  },

  /**
   * Oftalmo: PAUSA o atendimento porque o paciente foi enviado pra dilatar
   * a pupila. Acumula o tempo já decorrido em `attendanceDurationSec`, volta
   * o status pra EM_ESPERA e marca `attendancePausedForDilation` — o
   * cronômetro congela e a ficha NÃO auto-reinicia ao reabrir (ver
   * AttendanceTimerCard). Diferente de `endAttendance`: NÃO conclui nem gera
   * briefing. Retoma via `resumeFromPause` (continua de onde parou).
   */
  async pauseAttendanceForDilation(
    clinicId: string,
    appointmentId: string,
  ): Promise<number> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = snap.data() as Appointment;
    const now = Date.now();
    const startedAt = current.attendanceStartedAt ?? now;
    const prior = current.attendanceDurationSec ?? 0;
    // Só soma o segmento ativo se de fato estava correndo (EM_ATENDIMENTO).
    const segment =
      current.status === 'EM_ATENDIMENTO'
        ? Math.max(0, Math.round((now - startedAt) / 1000))
        : 0;
    const accumulatedSec = prior + segment;
    await updateDoc(ref, {
      status: 'EM_ESPERA',
      attendanceStartedAt: startedAt,
      attendanceDurationSec: accumulatedSec,
      attendancePausedForDilation: true,
      // Carimba quando foi dilatar — `pickNextUp` compara com o início de
      // atendimento dos demais pra decidir quando este vira o "próximo".
      dilationPausedAt: now,
      updatedAt: serverTimestamp(),
    });
    return accumulatedSec;
  },

  /**
   * Retoma um atendimento PAUSADO (dilatação OU ficha fechada). Volta status
   * pra EM_ATENDIMENTO e desloca `attendanceStartedAt` pra `now - acumulado`,
   * pra o cronômetro continuar de onde parou (sem contar o tempo pausado).
   * Mesma matemática do `resumeAttendance`, mas a partir de EM_ESPERA +
   * `attendancePausedForDilation`/`attendancePausedForClose`. Limpa os dois
   * motivos de pausa — inofensivo limpar o que não estava setado.
   */
  async resumeFromPause(
    clinicId: string,
    appointmentId: string,
  ): Promise<number> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = snap.data() as Appointment;
    const accumulatedMs = Math.max(0, (current.attendanceDurationSec ?? 0) * 1000);
    const newStartedAt = Date.now() - accumulatedMs;
    await updateDoc(ref, {
      status: 'EM_ATENDIMENTO',
      attendanceStartedAt: newStartedAt,
      attendanceDurationSec: deleteField(),
      attendancePausedForDilation: deleteField(),
      dilationPausedAt: deleteField(),
      attendancePausedForClose: deleteField(),
      updatedAt: serverTimestamp(),
    });
    return newStartedAt;
  },

  /**
   * Médico FECHOU a ficha do paciente com o atendimento em curso: pausa o
   * cronômetro (acumula em `attendanceDurationSec`, volta status pra
   * EM_ESPERA, marca `attendancePausedForClose`). Reabrir a ficha retoma
   * sozinho via `resumeFromPause` (ver `AttendanceTimerCard`) — diferente da
   * dilatação, aqui não há espera manual: o paciente está na sala, só a
   * ficha estava fechada.
   *
   * No-op se o agendamento não estiver mais EM_ATENDIMENTO (ex.: o médico já
   * concluiu o atendimento no instante em que a ficha fechou) — evita
   * reabrir uma pausa por cima de um estado que já avançou.
   */
  async pauseAttendanceForClose(
    clinicId: string,
    appointmentId: string,
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const current = snap.data() as Appointment;
    if (current.status !== 'EM_ATENDIMENTO') return;
    const now = Date.now();
    const startedAt = current.attendanceStartedAt ?? now;
    const prior = current.attendanceDurationSec ?? 0;
    const segment = Math.max(0, Math.round((now - startedAt) / 1000));
    await updateDoc(ref, {
      status: 'EM_ESPERA',
      attendanceStartedAt: startedAt,
      attendanceDurationSec: prior + segment,
      attendancePausedForClose: true,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Todos os agendamentos NÃO-cancelados do paciente HOJE, ordenados por
   * relevância (EM_ATENDIMENTO > EM_ESPERA > CONFIRMADA > AGENDADA >
   * PENDENTE > CONCLUIDA) e, em empate, por horário. Ignora cancelados,
   * faltas e soft-deletes. Base pros selos do cabeçalho (convênio + tipos
   * do dia); o primeiro da lista é o agendamento "ativo".
   */
  async getTodayAppointmentsForPatient(
    clinicId: string,
    patientId: string,
  ): Promise<Appointment[]> {
    const snap = await getDocs(todayAppointmentsQuery(clinicId, patientId));
    return relevantTodayAppointments(snap.docs);
  },

  /**
   * Versão TEMPO REAL de `getTodayAppointmentsForPatient`: assina os
   * agendamentos de hoje do paciente e dispara `callback` a cada mudança
   * (novo agendamento, troca de convênio/tipo, status, cancelamento…).
   * Retorna a função de `unsubscribe`. Em erro, loga e devolve lista vazia.
   */
  subscribeTodayAppointmentsForPatient(
    clinicId: string,
    patientId: string,
    callback: (appointments: Appointment[]) => void,
  ): Unsubscribe {
    return onSnapshot(
      todayAppointmentsQuery(clinicId, patientId),
      (snap) => callback(relevantTodayAppointments(snap.docs)),
      (err) => {
        console.error('[appointments] subscribeTodayAppointmentsForPatient:', err);
        callback([]);
      },
    );
  },

  /**
   * Busca o agendamento mais relevante pra abrir/continuar atendimento
   * de um paciente hoje. Prioriza EM_ATENDIMENTO > AGENDADA/CONFIRMADA/
   * EM_ESPERA > CONCLUIDA. Ignora cancelados, faltas e soft-deletes.
   */
  async getActiveTodayAppointmentForPatient(
    clinicId: string,
    patientId: string,
  ): Promise<Appointment | null> {
    const all = await this.getTodayAppointmentsForPatient(clinicId, patientId);
    return all[0] ?? null;
  },

  /**
   * T092: Count overbookings for a specific slot.
   */
  async countOverbookings(
    clinicId: string,
    professionalId: string,
    date: number,
    startTime: string,
  ): Promise<number> {
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('professionalId', '==', professionalId),
      where('date', '==', date),
      where('startTime', '==', startTime),
      where('isOverbooking', '==', true),
      where('status', 'not-in', ['CANCELADA']),
    );
    const snap = await getDocs(q);
    return snap.size;
  },

  /**
   * T093: Check convênio quota for a professional's agenda this month.
   */
  async checkConvenioQuota(
    clinicId: string,
    insurerId: string,
    professionalId: string,
  ): Promise<boolean> {
    // Get insurer quota config
    const insurerDoc = await getDoc(doc(db, 'clinics', clinicId, 'insurers', insurerId));
    if (!insurerDoc.exists()) return true; // No insurer config = no limit

    const insurer = insurerDoc.data();
    const quotaKey = `agenda_${professionalId}`;
    const monthlyQuota = insurer.quotas?.[quotaKey] ?? insurer.quotas?.['default'];

    if (!monthlyQuota) return true; // No quota configured

    // Count this month's appointments for this convênio + professional
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).getTime();

    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('insurerId', '==', insurerId),
      where('professionalId', '==', professionalId),
      where('date', '>=', monthStart),
      where('date', '<=', monthEnd),
      where('status', 'not-in', ['CANCELADA']),
    );
    const snap = await getDocs(q);
    return snap.size < monthlyQuota;
  },

  /**
   * T095: Get slot status for a date range (color coding).
   * Returns slots with availability status: GREEN (free), YELLOW (partial), RED (full).
   */
  async getSlotStatuses(
    clinicId: string,
    professionalId: string,
    startDate: number,
    endDate: number,
    slotsPerDay: number,
  ): Promise<Map<string, 'GREEN' | 'YELLOW' | 'RED'>> {
    const appointments = await this.getAppointmentsByDateRange(clinicId, startDate, endDate);
    const profAppointments = appointments.filter(
      (a) => a.professionalId === professionalId && a.status !== 'CANCELADA',
    );

    const dateSlotCounts = new Map<string, number>();
    for (const apt of profAppointments) {
      const dateKey = new Date(apt.date).toISOString().split('T')[0];
      dateSlotCounts.set(dateKey, (dateSlotCounts.get(dateKey) || 0) + 1);
    }

    const statuses = new Map<string, 'GREEN' | 'YELLOW' | 'RED'>();
    for (const [dateKey, count] of dateSlotCounts) {
      const ratio = count / slotsPerDay;
      if (ratio >= 1) {
        statuses.set(dateKey, 'RED');
      } else if (ratio >= 0.5) {
        statuses.set(dateKey, 'YELLOW');
      } else {
        statuses.set(dateKey, 'GREEN');
      }
    }

    return statuses;
  },

  /**
   * T267: Create return appointment from within consultation.
   */
  async scheduleReturn(
    clinicId: string,
    currentAppointment: Appointment,
    returnDays: number,
  ): Promise<Appointment> {
    const returnDate = new Date(currentAppointment.date);
    returnDate.setDate(returnDate.getDate() + returnDays);

    return this.createAppointment(clinicId, {
      clinicId,
      unitId: currentAppointment.unitId,
      patientId: currentAppointment.patientId,
      professionalId: currentAppointment.professionalId,
      type: 'RETORNO',
      status: 'AGENDADA',
      date: returnDate.getTime(),
      startTime: currentAppointment.startTime,
      endTime: currentAppointment.endTime,
      isOverbooking: false,
      reminderSent: false,
      returnDays,
      notes: `Retorno de consulta ${currentAppointment.id}`,
      createdBy: currentAppointment.professionalId!,
    } as Omit<Appointment, 'id' | 'createdAt'>);
  },

  /**
   * Lê a flag `requireApprovalForExternalBookings` da config da agenda.
   * Usado pelo modal do agendador externo pra decidir se a consulta nasce
   * PENDENTE ou já AGENDADA. Default false (sem aprovação).
   *
   * Read-only — qualquer membro da clínica tem acesso (rules atuais).
   */
  async getRequireApprovalForExternalBookings(clinicId: string): Promise<boolean> {
    try {
      const snap = await getDoc(doc(db, 'clinics', clinicId, 'config', 'agenda'));
      if (!snap.exists()) return false;
      return !!(snap.data() as { requireApprovalForExternalBookings?: boolean }).requireApprovalForExternalBookings;
    } catch (err) {
      console.error('[appointments] failed to read agenda config:', err);
      return false;
    }
  },

  /**
   * Aprova um agendamento PENDENTE → AGENDADA. Falha se o status atual
   * não for PENDENTE (evita re-aprovações em sequência ou aprovar algo
   * já cancelado/concluído).
   */
  async approvePending(clinicId: string, appointmentId: string, actorUid: string): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = (snap.data() as { status?: string }).status;
    if (current !== 'PENDENTE') {
      throw new Error(`Não é possível aprovar — status atual é ${current ?? '(desconhecido)'}.`);
    }
    await updateDoc(ref, {
      status: 'AGENDADA',
      approvedAt: Date.now(),
      approvedBy: actorUid,
    });
  },

  /**
   * Recusa um agendamento PENDENTE → CANCELADA com motivo. Mesma trava
   * de status atual = PENDENTE.
   */
  async rejectPending(
    clinicId: string,
    appointmentId: string,
    actorUid: string,
    reason: string,
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Agendamento não encontrado.');
    const current = (snap.data() as { status?: string }).status;
    if (current !== 'PENDENTE') {
      throw new Error(`Não é possível recusar — status atual é ${current ?? '(desconhecido)'}.`);
    }
    await updateDoc(ref, {
      status: 'CANCELADA',
      cancellationReason: `[Recusa de aprovação] ${reason.trim().slice(0, 500)}`,
      cancelledAt: Date.now(),
      cancelledBy: `STAFF:${actorUid}`,
    });
  },

  /**
   * Marca um agendamento como "próximo a ser atendido" — usado pela
   * secretária pra sinalizar pro médico quem chamar a seguir. Garante
   * que apenas UM agendamento por dia fica com a flag: desmarca todos
   * os outros do mesmo dia em batch atômico.
   *
   * Status terminais (CONCLUIDA/CANCELADA/FALTOU/EM_ATENDIMENTO) não
   * podem ser marcados — caller deve filtrar antes de chamar.
   */
  async markAsNextUp(
    clinicId: string,
    appointmentId: string,
    actorUid: string,
  ): Promise<void> {
    const targetRef = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const targetSnap = await getDoc(targetRef);
    if (!targetSnap.exists()) throw new Error('Agendamento não encontrado.');
    const target = targetSnap.data() as Appointment;

    const dayStart = new Date(target.date);
    dayStart.setHours(0, 0, 0, 0);
    const dayStartMs = dayStart.getTime();
    const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000 - 1;

    // Acha outros já marcados como próximo. Igualdade simples num único
    // campo usa o índice automático do Firestore — não exige índice
    // composto (o range em `date` + igualdade em `isNextUp` exigiria, e
    // ele não existe). Como a flag é limpa agressivamente e só vale 1
    // por dia, isso retorna ~1 doc; o filtro de dia roda no cliente.
    const others = await getDocs(query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('isNextUp', '==', true),
    ));

    const batch = writeBatch(db);
    for (const d of others.docs) {
      if (d.id === appointmentId) continue;
      // Mantém a semântica "1 por dia": só desmarca os do mesmo dia.
      const other = d.data() as Appointment;
      if (other.date < dayStartMs || other.date > dayEndMs) continue;
      batch.update(d.ref, {
        isNextUp: deleteField(),
        nextUpMarkedAt: deleteField(),
        nextUpMarkedBy: deleteField(),
        updatedAt: serverTimestamp(),
      });
    }
    batch.update(targetRef, {
      isNextUp: true,
      nextUpMarkedAt: Date.now(),
      nextUpMarkedBy: actorUid,
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
  },

  /**
   * Remove a marcação de "próximo a ser atendido". Idempotente — não
   * falha se já estiver desmarcado.
   */
  async unmarkAsNextUp(
    clinicId: string,
    appointmentId: string,
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      isNextUp: deleteField(),
      nextUpMarkedAt: deleteField(),
      nextUpMarkedBy: deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Define (ou remove, com `null`) o acompanhante do dia de um agendamento —
   * snapshot id + nome + CPF em claro, o MESMO formato que o "Novo
   * Agendamento" grava. É deste campo que o vínculo flui pro "acompanhante
   * do dia" da sessão quando a ficha do paciente abre (PatientDetailPage).
   * Usado pelo alerta de acompanhante ao marcar o paciente como "próximo".
   */
  async setAccompanist(
    clinicId: string,
    appointmentId: string,
    accompanist: { id: string; name: string; cpf?: string } | null,
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      accompanistId: accompanist ? accompanist.id : deleteField(),
      accompanistName: accompanist ? accompanist.name : deleteField(),
      accompanistCpf: accompanist?.cpf ?? deleteField(),
      // Registra que a pergunta foi respondida — inclusive "entra sozinho"
      // (accompanist null). O gate do "Avisar médico" lê este marcador.
      accompanistAsked: true,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Troca o convênio de um agendamento já existente. Passe `null` pra
   * voltar a "Particular" — nesse caso o campo `insurerId` é REMOVIDO do
   * doc via `deleteField()` (updateAppointment só filtra `undefined`, não
   * apaga o campo, então o convênio antigo persistiria).
   *
   * Registra a troca no histórico de alterações com rótulos legíveis
   * (nome do convênio / "Particular", não o id cru) — a mudança impacta a
   * cobrança e a tabela de preços, então vale auditar quem trocou e quando.
   *
   * Não revalida cota do convênio: trocar convênio costuma ser correção
   * administrativa (paciente apresentou a carteirinha certa depois) e não
   * deve ser bloqueado por cota mensal cheia.
   */
  async changeInsurer(
    clinicId: string,
    appointmentId: string,
    newInsurerId: string | null,
    opts: { actorUid: string; actorName?: string; fromLabel: string; toLabel: string },
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    // Troca no detalhe é sempre escolha deliberada → grava o marcador
    // positivo (limpa o alerta de "não selecionado").
    await updateDoc(ref, {
      insurerId: newInsurerId ?? deleteField(),
      insurerChoice: newInsurerId ? 'CONVENIO' : 'PARTICULAR',
      updatedAt: serverTimestamp(),
    });
    if (opts.fromLabel !== opts.toLabel) {
      await AppointmentHistoryService.record(clinicId, appointmentId, {
        changes: { insurerId: { from: opts.fromLabel, to: opts.toLabel } },
        action: 'convenio',
        actorUid: opts.actorUid,
        actorName: opts.actorName,
      });
    }
  },

  /**
   * Troca o MÉDICO responsável (`professionalId`) de um agendamento já
   * marcado — a recepção marcou pro médico errado, ou um cobre o outro.
   * Registra no histórico (ação 'medico') com os NOMES, não os uids.
   *
   * Se o paciente já chegou e está aguardando na sala de espera, a entrada
   * da fila acompanha (best-effort): é ela que decide de qual médico é a
   * chamada. Chamado/em atendimento fica como está — aí já é do médico
   * que chamou.
   */
  async changeProfessional(
    clinicId: string,
    appointmentId: string,
    next: { professionalId: string; professionalName?: string },
    opts: { actorUid: string; actorName?: string; fromLabel: string; toLabel: string },
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      professionalId: next.professionalId,
      updatedAt: serverTimestamp(),
    });
    if (opts.fromLabel !== opts.toLabel) {
      await AppointmentHistoryService.record(clinicId, appointmentId, {
        changes: { medico: { from: opts.fromLabel, to: opts.toLabel } },
        action: 'medico',
        actorUid: opts.actorUid,
        actorName: opts.actorName,
      });
    }
    try {
      const snap = await getDocs(query(
        collection(db, 'clinics', clinicId, 'queue'),
        where('appointmentId', '==', appointmentId),
      ));
      await Promise.all(
        snap.docs
          .filter((d) => (d.data() as { status?: string }).status === 'WAITING')
          .map((d) => updateDoc(d.ref, {
            professionalId: next.professionalId,
            professionalName: next.professionalName ?? deleteField(),
          })),
      );
    } catch (err) {
      console.warn('[appointments] fila não acompanhou a troca de médico:', err);
    }
  },

  /**
   * Troca o TIPO do agendamento — tipo base (enum) + subtipo customizado da
   * clínica (`appointmentTypeId`), editável a qualquer momento no detalhe.
   * `appointmentTypeId=null` limpa o subtipo. Registra no histórico (ação
   * 'tipo') com rótulos legíveis. A coluna "Tipo" da agenda reflete na hora.
   */
  async changeType(
    clinicId: string,
    appointmentId: string,
    next: { type: Appointment['type']; appointmentTypeId: string | null },
    opts: { actorUid: string; actorName?: string; fromLabel: string; toLabel: string },
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      type: next.type,
      appointmentTypeId: next.appointmentTypeId ?? deleteField(),
      updatedAt: serverTimestamp(),
    });
    if (opts.fromLabel !== opts.toLabel) {
      await AppointmentHistoryService.record(clinicId, appointmentId, {
        changes: { type: { from: opts.fromLabel, to: opts.toLabel } },
        action: 'tipo',
        actorUid: opts.actorUid,
        actorName: opts.actorName,
      });
    }
  },

  /**
   * Remarca um agendamento — troca a data e/ou o horário (início/fim).
   * Registra a mudança no histórico com rótulos legíveis (data BR +
   * intervalo de horário), em vez do epoch cru que o tracking automático
   * gravaria — mesma filosofia do `changeInsurer`.
   *
   * Não revalida encaixe nem cota: remarcar é correção administrativa e
   * quem decide sobre sobreposição é o staff (a UI já avisa de conflito
   * antes de salvar, sem bloquear — vira encaixe).
   */
  async reschedule(
    clinicId: string,
    appointmentId: string,
    next: { date: number; startTime: string; endTime: string },
    opts: {
      actorUid: string;
      actorName?: string;
      prev: { date: number; startTime: string; endTime: string };
    },
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, {
      date: next.date,
      startTime: next.startTime,
      endTime: next.endTime,
      updatedAt: serverTimestamp(),
    });
    const fmtSlot = (d: number, s: string, e: string) =>
      `${new Date(d).toLocaleDateString('pt-BR')} ${s}–${e}`;
    const fromLabel = fmtSlot(opts.prev.date, opts.prev.startTime, opts.prev.endTime);
    const toLabel = fmtSlot(next.date, next.startTime, next.endTime);
    if (fromLabel !== toLabel) {
      await AppointmentHistoryService.record(clinicId, appointmentId, {
        changes: { 'data/horário': { from: fromLabel, to: toLabel } },
        action: 'remarcacao',
        actorUid: opts.actorUid,
        actorName: opts.actorName,
      });
    }
  },
};
